import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import {
  useAgentIncidentsSetting,
  useUpdateAgentIncidentsSetting,
} from '../../api/agentSessions';
import AdminPageHeader from './AdminPageHeader';

/**
 * Admin → Standing orders: the organisation's standing orders for the AI
 * incident controller (issue #312).
 *
 * Free text, appended to the agent's charter on every wake, for every project
 * in the organisation — which assets matter most, what local practice explains
 * a detection, when not to notify anyone. Unlike the entitlement next door in
 * Tenant Capabilities, this is the organisation's own words to its own agent,
 * so any tenant admin may write it; a project manager can add project-specific
 * orders in that project's settings.
 */

const EXAMPLES = [
  'The assets we have tagged as critical matter most — weigh a threat to them above anything else.',
  'Through the cooler months, most detections on farmland near us are permitted burn-offs — check the burn calendar before treating one as a new fire.',
  'Do not notify anyone for a single detection more than 20 km from an asset unless the forecast fire danger is severe or above.',
  'Any detection inside one of our easements is critical, whatever its size.',
];

export default function AgentInstructionsManagement() {
  const { data: setting, isLoading } = useAgentIncidentsSetting();
  const update = useUpdateAgentIncidentsSetting();
  const [text, setText] = useState('');
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Server value seeds the editor once it arrives; typing then owns the box
  // until a save round-trips (the saved value comes back identical, so this
  // does not clobber in-flight edits).
  const serverText = setting?.agent_instructions ?? '';
  useEffect(() => {
    setText(serverText);
  }, [serverText]);

  if (isLoading || !setting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const max = setting.max_instructions_chars ?? 4000;
  const tooLong = text.length > max;
  const dirty = text !== serverText;

  const handleSave = () => {
    update.mutate(
      { agent_instructions: text },
      {
        onSuccess: () =>
          setSnackbar(
            text.trim()
              ? 'Standing orders saved — agent sessions pick them up on their next wake'
              : 'Standing orders cleared — the agent runs on its charter alone'
          ),
        onError: (err) => setSnackbar(err?.message || 'Could not save the standing orders'),
      }
    );
  };

  const included = setting.allow_agent_incidents;

  return (
    <Box>
      <AdminPageHeader
        title="Standing orders"
        description="What the incident agent should always do, and never do, when it monitors a fire for you."
        audience="admins"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* Left: the orders themselves */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            In your own words. These are added to the agent's briefing on every
            incident in your organisation: what you care about most, what local
            practice explains a detection, and when you do — or do not — want
            people notified.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <TextField
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={14}
              fullWidth
              placeholder="e.g. The assets we have tagged as critical matter most — weigh a threat to them above anything else."
              error={tooLong}
              helperText={
                tooLong
                  ? `${text.length} / ${max} characters — too long to save`
                  : `${text.length} / ${max} characters`
              }
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!dirty || tooLong || update.isPending}
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="text"
                onClick={() => setText(serverText)}
                disabled={!dirty || update.isPending}
              >
                Discard changes
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Right: plan status and guidance, kept in view while writing */}
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 }, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SmartToyIcon fontSize="small" color={included ? 'success' : 'disabled'} />
              <Typography variant="subtitle2">AI incident monitoring</Typography>
              <Chip
                size="small"
                label={included ? 'Included' : 'Not included'}
                color={included ? 'success' : 'default'}
                variant={included ? 'filled' : 'outlined'}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {included
                ? 'Included in your plan. People can hand a detection to the agent from an incident.'
                : 'Not included in your plan. Contact FiSci to add it. You can still write your standing orders now — they take effect as soon as it is added.'}
            </Typography>
            {included && !setting.environment_configured && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Included, but this environment is not set up for the agent yet — nothing
                will appear until it is.
              </Alert>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What works well
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Specific, checkable statements. The agent follows them alongside its
              standing charter — they can raise or lower its threshold for waking
              people, but they cannot stop it reporting a life-safety threat it can
              evidence, and it records in each incident log when an instruction
              decided what it did.
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
              {EXAMPLES.map((example) => (
                <Typography
                  key={example}
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.75 }}
                >
                  “{example}”
                </Typography>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
